import {
  AdditiveBlending,
  Color,
  InstancedBufferAttribute,
  InstancedBufferGeometry,
  Mesh,
  RingGeometry,
  ShaderMaterial,
  UniformsLib,
  UniformsUtils,
} from 'three';
import { Palette } from '../Palette.js';
import { GLSL_CONSTANTS } from '../shaders/glslCommon.js';

/**
 * Concentric glyph rings spread through depth, all in one draw call.
 *
 * Two jobs. First, parallax: the mandala disc is flat, so without something
 * occupying real z the camera's breathing and drift would be invisible. These
 * rings are what make the motion legible. Second, they are the surface that scene
 * fog acts on — `FogExp2` needs objects with genuine depth extent to have anything
 * to attenuate, and the rings span roughly seventy units toward the camera's far
 * plane.
 *
 * Per-instance radius, depth, spin, dash count and colour live in instanced
 * attributes and are resolved in the vertex shader, so the CPU writes nothing per
 * frame; the rings rotate purely as a function of `uTime`.
 */

/**
 * These rings are scenery, not emitters.
 *
 * At full brightness they crossed the bloom threshold and became the frame's
 * largest light source — a bright band 400-550 px out that lifted the whole
 * background. The brief is explicit that only the mandala and the ripples should
 * glow, so the rings are dimmed until they read as structure catching the
 * mandala's light rather than producing their own.
 */
const STRUCTURE_GAIN = 0.3;

export interface MandalaRingsOptions {
  readonly count?: number;
  readonly innerRadius?: number;
  readonly outerRadius?: number;
  readonly nearZ?: number;
  readonly farZ?: number;
  readonly seed?: number;
}

const VERTEX = /* glsl */ `
${GLSL_CONSTANTS}

attribute float aRadius;
attribute float aZ;
attribute float aSpin;
attribute float aSegments;
attribute float aBright;
attribute vec3  aColor;

uniform float uTime;
uniform float uBreath;

varying float vAngle;
varying float vSegments;
varying float vBright;
varying vec3  vColor;

#include <fog_pars_vertex>

void main() {
  // Angle is taken before the spin so the dashes are fixed to the ring and turn
  // with it, rather than sliding around a stationary ring.
  vAngle = atan(position.y, position.x);
  vSegments = aSegments;
  vBright = aBright;
  vColor = aColor;

  float spin = uTime * aSpin;
  float c = cos(spin);
  float s = sin(spin);
  mat2 rotation = mat2(c, -s, s, c);
  vec2 planar = rotation * position.xy * (aRadius * (1.0 + uBreath * 0.012));

  vec4 mvPosition = modelViewMatrix * vec4(planar, aZ, 1.0);
  gl_Position = projectionMatrix * mvPosition;

  #include <fog_vertex>
}
`;

const FRAGMENT = /* glsl */ `
${GLSL_CONSTANTS}

varying float vAngle;
varying float vSegments;
varying float vBright;
varying vec3  vColor;

#include <fog_pars_fragment>

void main() {
  // Break each ring into dashes. Soft ends keep them from shimmering when a ring
  // is nearly edge-on or very small on screen.
  float phase = fract((vAngle / TAU) * vSegments);
  float dash = smoothstep(0.05, 0.17, phase) * (1.0 - smoothstep(0.70, 0.86, phase));

  gl_FragColor = vec4(vColor * dash * vBright, 1.0);

  // With a near-black fog colour this fades distant rings toward nothing, which
  // under additive blending is exactly "dissolves into the dark".
  #include <fog_fragment>
}
`;

export class MandalaRings {
  readonly object: Mesh;
  private readonly material: ShaderMaterial;
  private readonly geometry: InstancedBufferGeometry;

  constructor(options: MandalaRingsOptions = {}) {
    const count = options.count ?? 9;
    const innerRadius = options.innerRadius ?? 12;
    const outerRadius = options.outerRadius ?? 48;
    const nearZ = options.nearZ ?? 4;
    const farZ = options.farZ ?? -72;
    const random = mulberry32(options.seed ?? 0x21a7);

    const base = new RingGeometry(0.988, 1.0, 240);
    this.geometry = new InstancedBufferGeometry();
    this.geometry.index = base.index;
    this.geometry.setAttribute('position', base.getAttribute('position'));
    this.geometry.instanceCount = count;
    base.dispose();

    const radii = new Float32Array(count);
    const depths = new Float32Array(count);
    const spins = new Float32Array(count);
    const segments = new Float32Array(count);
    const brightness = new Float32Array(count);
    const colors = new Float32Array(count * 3);
    const tint = new Color();

    for (let index = 0; index < count; index += 1) {
      const t = count === 1 ? 0 : index / (count - 1);
      radii[index] = innerRadius + (outerRadius - innerRadius) * t;
      depths[index] = nearZ + (farZ - nearZ) * t;
      // Alternating direction, and rates that share no common period so the set
      // never realigns into a pose the viewer has already seen.
      spins[index] = (index % 2 === 0 ? 1 : -1) * (0.011 + random() * 0.043);
      segments[index] = Math.round(10 + random() * 62);
      // Distant rings start dimmer; fog then finishes the job.
      brightness[index] = (0.55 + random() * 0.5) * (1 - t * 0.45) * STRUCTURE_GAIN;

      tint.copy(Palette.ringCool).lerp(Palette.ringWarm, t * 0.85 + random() * 0.15);
      colors[index * 3] = tint.r;
      colors[index * 3 + 1] = tint.g;
      colors[index * 3 + 2] = tint.b;
    }

    this.geometry.setAttribute('aRadius', new InstancedBufferAttribute(radii, 1));
    this.geometry.setAttribute('aZ', new InstancedBufferAttribute(depths, 1));
    this.geometry.setAttribute('aSpin', new InstancedBufferAttribute(spins, 1));
    this.geometry.setAttribute('aSegments', new InstancedBufferAttribute(segments, 1));
    this.geometry.setAttribute('aBright', new InstancedBufferAttribute(brightness, 1));
    this.geometry.setAttribute('aColor', new InstancedBufferAttribute(colors, 3));

    this.material = new ShaderMaterial({
      // Fog uniforms have to be merged in explicitly: `fog: true` makes three
      // define USE_FOG, but a raw ShaderMaterial supplies its own uniform block.
      uniforms: UniformsUtils.merge([
        UniformsLib['fog'] ?? {},
        {
          uTime: { value: 0 },
          uBreath: { value: 0 },
        },
      ]),
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
      fog: true,
    });

    this.object = new Mesh(this.geometry, this.material);
    // The base ring's bounding sphere has radius 1; instances are scaled up to 48
    // in the shader, so three's culling test would be wrong. Nothing to cull here
    // in any case.
    this.object.frustumCulled = false;
    this.object.renderOrder = 5;
  }

  update(time: number, breath: number): void {
    this.material.uniforms['uTime']!.value = time;
    this.material.uniforms['uBreath']!.value = breath;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}

function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
